import dayjs from 'dayjs';

import type {CourseExpandedCommon} from '../../../api/course';
import type {BookingPeriod} from '../types';

import {dateTimeObjToTimestamp} from '../../../utils/datetime';
import {
  getCourseDisplayFields,
  isCourseArchived,
  isCourseCorporate,
  isCourseEvergreen,
} from '../../../utils/entity/course';
import {findCurrentPeriodForRestartingCourse} from './findCurrentPeriodForRestartingCourse';

export function getPeriods(course: CourseExpandedCommon): BookingPeriod[] {
  if (isCourseArchived(course)) {
    return [];
  }

  if (isCourseEvergreen(course)) {
    return getPeriodsForEvergreenCourse(course);
  }

  if (isCourseCorporate(course)) {
    return getPeriodsForCorporateCourse(course);
  }

  return getPeriodsForRegularCourse(course);
}

function getPeriodsForCorporateCourse(
  course: CourseExpandedCommon,
): BookingPeriod[] {
  const currentPeriod = findCurrentPeriodForRestartingCourse(course);

  let endDate = dayjs(currentPeriod.end);

  let finalDate = dayjs(dateTimeObjToTimestamp(course.program_end_date!));

  if (course.subtenant?.auto_restart_on.includes('conference')) {
    /**
     * this is part of workaround to add ability
     * to book conference outside of BIG cycle
     * to trigger auto restart based on existing of the booked conference
     */
    finalDate = finalDate.add(
      course.subtenant?.limits?.conference_booking_days ?? 0,
      'days',
    );
  }

  const cycleDuration =
    getCourseDisplayFields(course).duration ?? course.edition?.duration ?? 0;
  const periods: BookingPeriod[] = [currentPeriod];

  while (endDate.isBefore(finalDate)) {
    const start = endDate;

    endDate = start.add(cycleDuration, 'days');

    periods.push({
      end: endDate.toDate().valueOf(),
      start: start.toDate().valueOf(),
    });
  }

  return periods;
}

function getPeriodsForEvergreenCourse(
  course: CourseExpandedCommon,
): BookingPeriod[] {
  const currentPeriod = findCurrentPeriodForRestartingCourse(course);

  return [currentPeriod];
}

function getPeriodsForRegularCourse(
  course: CourseExpandedCommon,
): BookingPeriod[] {
  return [
    {
      end: dateTimeObjToTimestamp(course.end_date!),
      start: dateTimeObjToTimestamp(course.start_date!),
    },
  ];
}
